import React, { useState } from "react";
import { useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";
import { Box, CircularProgress, Container, Typography, useMediaQuery, useTheme, Stack, Grid } from "@mui/material";
import { Skeleton } from "@mui/material";
import HeroSection from "../../components/common/HeroSection";
import Conversation from "./Conversation";
import { getForexServicePage } from "../../services/ForexServicePageService";

const ForexService = () => {
  const { country_id, country_name } = useSelector((state) => state.auth || {});
  const [activeTab, setActiveTab] = useState("right");

  const { data: programData, isLoading: loading } = useQuery({
    queryKey: ["forexServicePage", country_id],
    queryFn: async () => {
      if (!country_id) return null;
      const res = await getForexServicePage(country_id);
      if (res?.data?.success && res?.data?.data) {
        return res.data.data;
      }
      return null;
    },
    enabled: !!country_id,
  });

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const getResponsiveHtml = (html) => {
    if (!html) return "";
    if (!isMobile) return html.replace(/<br\s*\/?>/gi, "").replace(/&nbsp;/gi, " ").replace(/\u00A0/gi, " ").replace(/\[empty_line\]/gi, "<br />");
    return html
      .replace(/font-size:\s*\d+px/gi, "font-size: 18px")
      .replace(/font-weight:\s*900/gi, "font-weight: 700")
      .replace(/<br\s*\/?>/gi, "")
      .replace(/&nbsp;/gi, " ")
      .replace(/\u00A0/gi, " ")
      .replace(/\[empty_line\]/gi, "<br />");
  };

  const parseForexComparison = (html) => {
    if (!html) return null;
    const temp = document.createElement("div");
    temp.innerHTML = html;
    const rows = Array.from(temp.querySelectorAll("p")).map(p =>
      p.innerText.replace(/\s+/g, " ").trim()
    );
    if (!rows[0]?.includes("Forex")) return null;
    return rows.map((row, index) => {
      if (index === 0) {
        const parts = row.split("GLAPSOL Forex");
        return { left: parts[0]?.trim(), right: "GLAPSOL Forex", isHeader: true };
      }
      const splitIndex = row.lastIndexOf("  ");
      if (splitIndex !== -1) {
        return { left: row.substring(0, splitIndex).trim(), right: row.substring(splitIndex).trim(), isHeader: false };
      }
      const keywords = ["Fully", "No agents", "Transparent"];
      const keyword = keywords.find(k => row.includes(k));
      if (keyword) {
        const parts = row.split(keyword);
        return { left: parts[0].trim(), right: keyword + parts[1], isHeader: false };
      }
      return { full: row, isFull: true };
    });
  };

  if (loading) {
    return (
      <Container
        sx={{
          py: { xs: 3, sm: 4, md: 6 },
          px: { xs: 3, sm: 4, md: 6 },
        }}
      >
        <Grid container spacing={2} alignItems="center">

          {/* Left skeleton */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Skeleton variant="text" height={60} width="80%" />
            <Skeleton variant="text" height={30} width="90%" />
            <Skeleton variant="text" height={30} width="85%" />

            <Stack direction="row" spacing={2} mt={3}>
              <Skeleton variant="rounded" width={140} height={45} />
              <Skeleton variant="rounded" width={140} height={45} />
            </Stack>

            <Stack spacing={2} mt={3}>
              <Skeleton variant="text" width="70%" />
              <Skeleton variant="text" width="60%" />
              <Skeleton variant="text" width="75%" />
            </Stack>
          </Grid>

          {/* Right skeleton */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Skeleton
              variant="rectangular"
              height={420}
              sx={{ borderRadius: 2 }}
            />
          </Grid>

        </Grid>
      </Container>
    );
  }

  // if (loading)
  //   return (
  //     <Box sx={{ display: "flex", justifyContent: "center", mt: 10 }}>
  //       <CircularProgress color="warning" />
  //     </Box>
  //   );

  if (!programData)
    return (
      <Box sx={{ textAlign: "center", mt: 10 }}>
        <p>No forex data found for {country_name || "selected country"}.</p>
      </Box>
    );

  const heroTitle = programData.hero_section_title || "";
  const heroSubtitle = programData.hero_section_desc || "";
  const heroImage = programData.hero_section_image || "";
  const heroImageType = programData.hero_section_image_type || "image";
  const about_title = programData.about_title || "";
  const about_desc = programData.about_desc || "";
  const about_short_desc = programData.about_short_desc || "";

  const comparisonData = parseForexComparison(about_desc);
  const headerRow = comparisonData?.find((row) => row.isHeader);
  const tabs = [
    { key: "left", label: headerRow?.left },
    { key: "right", label: headerRow?.right },
  ];

  return (
    <>
      <HeroSection
        titleContent={heroTitle}
        subtitleContent={heroSubtitle}
        heroImage={heroImage}
        heroImageType={heroImageType}
      />
      {about_title && about_desc && (
        <Container sx={{ py: { xs: 2, sm: 2, md: 3 }, px: { xs: 3, sm: 4, md: 6 }, paddingLeft: { xs: "24px !important", md: "48px !important" }, paddingRight: { xs: "24px !important", md: "48px !important" } }}>
          {comparisonData ? (
            isMobile ? (
              <Box sx={{ borderRadius: "16px", background: "#fff", overflow: "hidden", boxShadow: "0 8px 20px rgba(0,0,0,0.05)" }}>
                <Box sx={{ display: "flex", background: "#f1f5f9", p: "4px", borderRadius: "12px", m: 2 }}>
                  {tabs.map((tab) => (
                    <Box key={tab.key} onClick={() => setActiveTab(tab.key)} sx={{ flex: 1, textAlign: "center", py: 1, borderRadius: "10px", cursor: "pointer", background: activeTab === tab.key ? "#fd0000" : "transparent", color: activeTab === tab.key ? "#fff" : "#000", fontWeight: 600, transition: "all 0.3s ease" }}>
                      {tab.label}
                    </Box>
                  ))}
                </Box>
                <Box sx={{ p: 2 }}>
                  {comparisonData.map((row, i) => {
                    if (row.isHeader || row.isFull) return null;
                    const isRight = activeTab === "right";
                    return (
                      <Box key={i} sx={{ display: "flex", alignItems: "flex-start", gap: 1, mb: 2 }}>
                        <Typography sx={{ fontSize: 14, lineHeight: 1.5, color: isRight ? "#000" : "#6b7280" }}>
                          {isRight ? row.right : row.left}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
                {comparisonData.filter((row) => row.isFull).map((row, i) => (
                  <Box key={i} sx={{ p: 2, background: "#f9fafb", textAlign: "center" }}>
                    <Typography sx={{ fontSize: 14, color: "#000", lineHeight: 1.7, fontWeight: 500 }}>{row.full}</Typography>
                  </Box>
                ))}
              </Box>
            ) : (
              <Box sx={{ borderRadius: "16px", overflow: "hidden", background: "#ffffff", border: "1px solid #f1f5f9" }}>
                {comparisonData.map((row, i) =>
                  row.isHeader ? (
                    <Box key={i} sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", background: "#002045", color: 'white', textAlign: "left", fontWeight: 800 }}>
                      <Typography sx={{ px: 2, py: 1.5, fontWeight: 800, borderRight: "1px solid #000000" }}>{row.left}</Typography>
                      <Typography sx={{ px: 2, py: 1.5, fontWeight: 800 }}>{row.right}</Typography>
                    </Box>
                  ) : (
                    <Box key={i} sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", alignItems: "stretch", borderBottom: "1px solid #e5e7eb" }}>
                      <Box sx={{ px: 2, py: 1.5, borderRight: "1px solid #e5e7eb", display: "flex", alignItems: "center" }}><Typography>{row.left}</Typography></Box>
                      <Box sx={{ px: 2, py: 1.5, display: "flex", alignItems: "center" }}><Typography sx={{ fontWeight: 500 }}>{row.right}</Typography></Box>
                    </Box>
                  )
                )}
              </Box>
            )
          ) : (
            <Typography sx={{ textAlign: "justify", color: "#000" }} dangerouslySetInnerHTML={{ __html: getResponsiveHtml(about_desc) }} />
          )}
        </Container>
      )}
      <Conversation about_short_desc={about_short_desc} />
    </>
  );
};

export default ForexService;
